import { Editor, Element, Path, Transforms } from 'slate';
import type { SlateEditor } from 'platejs';

import { options } from 'common/components/ContentEditor/options';
import {
    asEditor,
    unwrapList,
    type TTypedElement,
} from 'common/components/ContentEditor/utils/editorHelpers';

const isLi = (n: unknown) =>
    Element.isElement(n) && (n as TTypedElement).type === options.li.type;

/**
 * Plain block plugins have no list semantics, so pressing Enter inside a
 * list item falls through to Slate's default `insertBreak` — splitting the
 * paragraph, but leaving both halves inside the same `li` instead of
 * starting a new list item. This makes Enter split at the `li` level
 * instead, matching how every other editor treats lists, and exits the
 * list when pressed on an already-empty item.
 */
export const withListEnter = ({
    editor,
    tf: { insertBreak },
}: {
    editor: Record<string, unknown>;
    tf: { insertBreak: () => void };
}) => {
    const slateEditor = asEditor(editor as SlateEditor);

    return {
        transforms: {
            insertBreak() {
                const [liEntry] = Editor.nodes(slateEditor, { match: isLi });

                if (!liEntry) {
                    insertBreak();

                    return;
                }

                const [, liPath] = liEntry;

                if (!Editor.string(slateEditor, liPath)) {
                    unwrapList(editor as SlateEditor);

                    return;
                }

                Transforms.splitNodes(slateEditor, {
                    match: isLi,
                    always: true,
                });

                // splitNodes leaves editor.selection pointing at the new
                // second `li`, but Slate-React's native DOM selection isn't
                // resynced to match by the time the next keystroke arrives
                // (it's still resolved from before the split). Deferring
                // the correction lets that settle first.
                const nextPoint = Editor.start(slateEditor, Path.next(liPath));

                setTimeout(() => {
                    Transforms.select(slateEditor, nextPoint);
                }, 0);
            },
        },
    };
};
